import moment from "moment";
import {
  getPublishItemCounts,
  getPublishPeriodDetails,
} from "./eventsWorkspacePublish.utils";

const EVENT_SHIFT_VIEW = {
  DAILY: "3",
  MONTHLY: "2",
  WEEKLY: "4",
};

/**
 * Maps clearable items and/or Event/Shift sidebar filter to DeleteRange `IsShift`.
 * - shifts only → true
 * - events only → false
 * - both / empty → null
 * Explicit one-type sidebar filter wins; otherwise derives from items being cleared.
 * @param {{
 *   clearableItems?: Array<{ isShift?: boolean }>,
 *   eventShiftFilter?: string[],
 * }} [options]
 * @returns {boolean|null}
 */
export function resolveDeleteRangeIsShift({
  clearableItems = [],
  eventShiftFilter = [],
} = {}) {
  if (eventShiftFilter?.length) {
    const hasEvent = eventShiftFilter.includes("event");
    const hasShift =
      eventShiftFilter.includes("shift") ||
      eventShiftFilter.includes("shift-others");

    if (hasEvent && !hasShift) return false;
    if (hasShift && !hasEvent) return true;
    // Both event + shift selected → treat as clear both.
    if (hasEvent && hasShift) return null;
  }

  if (clearableItems?.length) {
    const { shiftCount, eventCount } = getPublishItemCounts(clearableItems);
    if (shiftCount > 0 && eventCount > 0) return null;
    if (shiftCount > 0) return true;
    if (eventCount > 0) return false;
  }

  return null;
}

/**
 * Inclusive calendar period for DeleteRange (YYYY-MM-DD).
 * @param {string} calendarViewId
 * @param {Date|string} date
 * @returns {{ startDate: string, endDate: string }}
 */
export function getClearPeriodDateRange(calendarViewId, date) {
  const anchor = moment(date);
  if (!anchor.isValid()) {
    return { startDate: "", endDate: "" };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    const day = anchor.format("YYYY-MM-DD");
    return { startDate: day, endDate: day };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    return {
      startDate: anchor.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: anchor.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }

  return {
    startDate: anchor.clone().startOf("week").format("YYYY-MM-DD"),
    endDate: anchor.clone().endOf("week").format("YYYY-MM-DD"),
  };
}

/**
 * Period copy keys for the clear confirmation modal.
 * @param {string} calendarViewId
 * @param {Date|string} date
 */
export function getClearPeriodDetails(calendarViewId, date) {
  const publishDetails = getPublishPeriodDetails(calendarViewId, date);
  const introKeyByPublishKey = {
    "scheduler.publishEventShiftIntroDay": "scheduler.clearPeriodIntroDay",
    "scheduler.publishEventShiftIntroWeek": "scheduler.clearPeriodIntroWeek",
    "scheduler.publishEventShiftIntroMonth": "scheduler.clearPeriodIntroMonth",
  };
  const periodLabelKeyByPublishKey = {
    "scheduler.publishEventShiftPeriodDate": "scheduler.clearPeriodPeriodDate",
    "scheduler.publishEventShiftPeriodWeek": "scheduler.clearPeriodPeriodWeek",
    "scheduler.publishEventShiftPeriodMonth": "scheduler.clearPeriodPeriodMonth",
  };
  const titleKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.clearDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.clearWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.clearMonthly",
  };
  const confirmLabelKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.clearDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.clearWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.clearMonthly",
  };
  const confirmQuestionKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.clearPeriodConfirmQuestionDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.clearPeriodConfirmQuestionWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.clearPeriodConfirmQuestionMonth",
  };
  const descriptionKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.clearPeriodDescriptionDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.clearPeriodDescriptionWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.clearPeriodDescriptionMonth",
  };

  let periodValue = publishDetails.periodValue;
  const anchor = moment(date);
  if (anchor.isValid() && calendarViewId === EVENT_SHIFT_VIEW.WEEKLY) {
    const start = anchor.clone().startOf("week");
    const end = anchor.clone().endOf("week");
    periodValue = `${start.format("MMM DD, YYYY")} - ${end.format(
      "MMM DD, YYYY",
    )}`;
  } else if (anchor.isValid() && calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    periodValue = anchor.format("MMMM YYYY");
  }

  return {
    titleKey:
      titleKeyByView[calendarViewId] || "scheduler.clearWeek",
    confirmLabelKey:
      confirmLabelKeyByView[calendarViewId] || "scheduler.clearWeek",
    confirmQuestionKey:
      confirmQuestionKeyByView[calendarViewId] ||
      "scheduler.clearPeriodConfirmQuestionWeek",
    descriptionKey:
      descriptionKeyByView[calendarViewId] ||
      "scheduler.clearPeriodDescriptionWeek",
    introKey: introKeyByPublishKey[publishDetails.introKey],
    periodLabelKey: periodLabelKeyByPublishKey[publishDetails.periodLabelKey],
    periodValue,
  };
}

/**
 * Visible filtered events/shifts eligible for Clear (draft + published).
 * @param {Array<{ id?: number, isShift?: boolean }>} events
 * @returns {Array<{ id: number, isShift: boolean }>}
 */
export function collectClearableItemsFromEvents(events = []) {
  return (events || [])
    .filter((ev) => ev?.id != null)
    .map((ev) => ({ id: ev.id, isShift: Boolean(ev.isShift) }));
}

/**
 * @param {Array<{ schedulerEvents?: Array<object> }>} weekData
 * @returns {Array<{ id: number, isShift: boolean }>}
 */
export function collectClearableItemsFromWeekData(weekData = []) {
  const items = [];
  (weekData || []).forEach((day) => {
    items.push(...collectClearableItemsFromEvents(day?.schedulerEvents || []));
  });
  return items;
}

/**
 * Stable uniqueness key for visible clearable/draft item lists.
 * When `userId` is present (staff-name weekly view), staff cards stay distinct
 * for selection/visibility tracking. When `date` is present, day occurrences
 * stay distinct. Confirmation modal totals still use {@link getPublishItemCounts},
 * which counts unique events/occurrences (not per user).
 * @param {{ id?: number, date?: string, userId?: number|string|null }} item
 * @returns {string|null}
 */
export function getClearableItemDedupeKey(item) {
  if (item?.id == null) return null;
  const dateKey =
    item.date != null && item.date !== "" ? `_${item.date}` : "";
  if (Object.prototype.hasOwnProperty.call(item, "userId")) {
    const userKey =
      item.userId == null || item.userId === "" ? "unassigned" : item.userId;
    return `${item.id}${dateKey}_${userKey}`;
  }
  return `${item.id}${dateKey}`;
}

/**
 * @param {Array<{ id?: number, isShift?: boolean, date?: string, userId?: number|string|null }>} items
 * @returns {Array<{ id: number, isShift: boolean, date?: string, userId?: number|string|null }>}
 */
export function dedupeClearableItems(items = []) {
  const byKey = new Map();
  (items || []).forEach((item) => {
    const key = getClearableItemDedupeKey(item);
    if (key == null || byKey.has(key)) return;
    const next = { id: item.id, isShift: Boolean(item.isShift) };
    if (item.date != null && item.date !== "") {
      next.date = item.date;
    }
    if (Object.prototype.hasOwnProperty.call(item, "userId")) {
      next.userId = item.userId;
    }
    byKey.set(key, next);
  });
  return [...byKey.values()];
}

/**
 * Builds DeleteRange request body.
 * @param {{
 *   ownerId: number,
 *   calendarViewId: string,
 *   currentDate: Date|string,
 *   clearableItems?: Array<{ isShift?: boolean }>,
 *   eventShiftFilter?: string[],
 *   officeIds?: number[],
 *   userIds?: number[],
 * }} options
 */
export function buildDeleteRangePayload({
  ownerId,
  calendarViewId,
  currentDate,
  clearableItems = [],
  eventShiftFilter = [],
  officeIds = [],
  userIds = [],
}) {
  const { startDate, endDate } = getClearPeriodDateRange(
    calendarViewId,
    currentDate,
  );

  return {
    OwnerId: ownerId,
    StartDate: startDate,
    EndDate: endDate,
    IsShift: resolveDeleteRangeIsShift({
      clearableItems,
      eventShiftFilter,
    }),
    UserIds: userIds || [],
    OfficeIds: officeIds || [],
  };
}

export { getPublishItemCounts as getClearItemCounts };
